import React, { useContext, useMemo, useState } from "react";
import styled from "styled-components";
import MuiTable from "common/graphs/Table/Table";
import ActionsContext from "common/ActionsContext";
import useProfile from "common/hooks/useProfile";
import { NumberCell, TextWithIcon } from "common/graphs/Table/TableRenders";
import { safeStr } from "common/api";
import { durationToInt } from "common/utils";
import { RATE_PRECISIONS, renderLimitFromMbps } from "./utils";
import Modal from "common/Modal";

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const FooterError = styled.div`
  width: 100%;
  text-align: left;
  margin-bottom: 10px;
  margin-left: 5px;
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
}) => {
  const handleApply = () => {
    const cmds = items.reduce((acc, group) => {
      if (group !== "all-subscribers") {
        acc.push(`clear api subscriber-group ${safeStr(group)}`);
      }
      return acc;
    }, []);
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then(() => {
        setShowDeleteModal(false);
        whenDone();
      })
      .catch((error) => showModalError(error, ""));
  };
  if (!showDeleteModal) {
    return null;
  }
  const hasAllSubscribers = items.includes("all-subscribers");
  const title =
    items.length === 1
      ? `Delete ${items[0]} subscriber group?`
      : `Delete ${items.length - (hasAllSubscribers ? 1 : 0)} subscriber groups?`;
  

  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {items.length === 1 ? (
            <p>Deleting it will erase its statistics.</p>
          ) : null}
          {items.length > 1 ? (
            <>
              {items.length < 100 ? (
                <ul>
                  {items.map((name) => {
                    if(name === "all-subscribers"){
                      return null;
                    }
                    return <li key={name}>{name}</li>;
                  })}
                </ul>
              ) : null}
              <p>Deleting them will erase their statistics.</p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
      footerMessage={
        hasAllSubscribers ? (
          <FooterError className="color-red">
            Subscriber group "all-subscribers" cannot be deleted.
          </FooterError>
        ) : null
      }
    />
  );
};

const TableContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: auto;
  margin: 0;
  padding: 0;

  & > div {
    padding: 0 0 25px 0;
  }

  .muidataTable tbody td.warning {
    background: #cc0000;
    color: #fff !important;
  }
  &.colored {
    .muidataTable tbody td {
      &.warning {
        background: transparent;
      }
      &.hyperlink-text {
        cursor: pointer;
      }
      &.min-q1 {
        background: #3d85c6;
        color: #fff !important;
      }
      &.q1-median {
        background: #6aa84f;
        color: #fff !important;
      }
      &.q3-max {
        background: #cc0000;
        color: #fff !important;
      }
      &.median-q3 {
        background: #e69138;
        color: #fff !important;
      }
    }
  }
`;
const getWarningClass = ({
  threshold,
  value,
  mean,
  meanValue,
  inverted,
  label,
  percent,
}) => {
  if ((mean && meanValue < mean) || label === "MEAN-Mbps") {
    return "";
  }
  if (threshold === "auto") {
    if (percent !== undefined && parseFloat(value) >= percent && percent > 0) {
      return "warning";
    }
    return "";
  }
  if (!inverted) {
    if (parseFloat(threshold) && parseFloat(value) > parseFloat(threshold)) {
      return "warning";
    }
    return "";
  }
  if (parseFloat(threshold) && parseFloat(value) < parseFloat(threshold)) {
    return "warning";
  }

  return "";
};

const getQuantileClass = (_value, quantiles, inverted, groupName) => {
  if (
    !quantiles ||
    _value === "n/a" ||
    _value === null ||
    groupName === "all-subscribers"
  ) {
    return "";
  }
  const value = parseFloat(_value);
  const { q1, q3, median, min, max } = quantiles;
  if (!inverted) {
    if (value > q3 && value <= max) {
      return "q3-max";
    }
    if (value > median && value <= q3) {
      return "median-q3";
    }
    if (value > q1 && value <= median) {
      return "q1-median";
    }
    if (value >= min && value <= q1) {
      return "min-q1";
    }
    return "";
  } else {
    if (value > q3 && value <= max) {
      return "min-q1";
    }
    if (value > median && value <= q3) {
      return "q1-median";
    }
    if (value > q1 && value <= median) {
      return "median-q3";
    }
    if (value >= min && value <= q1) {
      return "q3-max";
    }
    return "";
  }
};

const quartileColorCellClassName = (value, row, column) => {
  const { quantiles, label, threshold, mean, inverted } = column;
  const groupName = row["SUBSCRIBER-GROUP"];
  const meanValue = parseFloat(row["MEAN-Mbps"]);
  const hyperlinkClass = label === "SUBS-ACTIVE" ? "hyperlink-text" : "";
  const warningClass = getWarningClass({
    threshold,
    value,
    mean,
    meanValue,
    inverted,
    label,
    percent: quantiles?.pcustom,
  });
  const quantileClass = getQuantileClass(value, quantiles, inverted, groupName);
  return `${hyperlinkClass} ${warningClass} ${quantileClass}`;
};

const COLUMNS_TO_CHECK = [
  { id: "RTT-80%-ms", label: "RTT-P80-ms", inverted: false },
  { id: "RTT-MED-ms", label: "RTT-MED-ms", inverted: false },
  { id: "RTT-MIN-ms", label: "RTT-MIN-ms", inverted: false },
  { id: "RTX", label: "RTX", inverted: false },
  { id: "CONGEST", label: "CONGESTION", inverted: false }
];

const getWarningCount = (thresholds, row, metricsQuantileData = {}) => {
  const meanValue = parseFloat(row["MEAN-Mbps"]);
  const mean = thresholds["MEAN-Mbps"];

  return COLUMNS_TO_CHECK.reduce(
    (count, col) =>
      getWarningClass({
        threshold: thresholds[col.id],
        value: row[col.id],
        inverted: col.inverted,
        label: col.label,
        mean,
        meanValue,
        percent: metricsQuantileData[col.id]?.pcustom,
      }) === "warning"
        ? count + 1
        : count,
    0,
  );
};

const getColumns = ({
  timePeriod,
  metric,
  direction,
  metricsQuantileData,
  thresholds,
  actions,
  onRemoved,
  hideRates,
  meanSorting,
  setDeleteSelected,
  setShowDeleteModal,
}) => [
  {
    label: "SUBSCRIBER-GROUP",
    id: "SUBSCRIBER-GROUP",
    cellClassName: "hyperlink-text",
    title: "Subscriber’s group",
    numeric: false,
    onClick: (subscriberGroup, row) => {
      globalNavigate("viewStatusSubscriberGroups", {
        subscriberGroup,
        timePeriod,
        metric,
        direction,
        returnView: "viewStatusSubscriberGroups",
        type: row["GROUP-TYPE"],
      });
    },
  },
  {
    label: "SUBS-ACTIVE",
    title: "SUBS-ACTIVE",
    id: "SUBS-ACTIVE",
    numeric: true,
    onClick: (_, row) => {
      globalNavigate("viewStatusSubscriberGroups", {
        subscriberGroup: row["SUBSCRIBER-GROUP"],
        timePeriod,
        metric,
        view: "qoe",
        direction,
        type: row["GROUP-TYPE"],
        returnView: "viewStatusSubscriberGroups",
      });
    },
    cellClassName: quartileColorCellClassName,
    quantiles: metricsQuantileData["SUBS-ACTIVE"],
  },
  {
    label: "FL-ACTIVE",
    id: "FL-ACTIVE",
    title: "FL Active",
    numeric: true,
  },
  {
    label: "FL-CREATED",
    id: "FL-CREATED",
    title: "FL Created",
    numeric: true,
  },
  {
    label: "LIMIT",
    id: "LIM-Mbps",
    title: "LIMIT",
    numeric: true,
    renderCell: (value) =>
      hideRates ? 'n/a' :
      renderLimitFromMbps(value),
  },
  ...(hideRates
    ? [
        {
          label: "RATE-POLICY",
          id: "POLICY-RATE",
          title: "RATE-POLICY",
          numeric: false,
          onClick: null,
          cellClassName: null,
          renderCell: (value, row) => {
            const icon = row["ASSIGNMENT"] === undefined ? "warning" : null;
            return <TextWithIcon icon={icon} value={value} />;
          },
        },
      ]
    : [
        {
          label: "RATE-POLICY",
          id: "POLICY-RATE",
          title: "RATE-POLICY",
          numeric: false,
          onClick: (_, row) => {
            const policyName = row["POLICY-RATE"];
            const assignment = row["ASSIGNMENT"];
            return policyName !== "n/a"
              ? actions.send("subscriber-group-policy-rate-clicked", {
                  policyName,
                  assignment,
                })
              : null;
          },
          renderCell: (value, row) => {
            const icon = row["ASSIGNMENT"] === undefined ? "warning" : null;
            return <TextWithIcon icon={icon} value={value} />;
          },
          cellClassName: (value, row) => {
            return `align-left${value !== "n/a" ? " hyperlink-text" : ""}${
              row["FL-CREATED"] === "undefined" ? " col-red" : ""
            }`;
          },
        },
      ]),
  {
    label: "MBYTES",
    id: "MBYTES",
    title: "MBytes",
    numeric: true,
    renderCell: (value) => (
      <NumberCell
        precision={RATE_PRECISIONS}
        value={value}
        hasNA={true}
      />
    ),
  },
  {
    label: "CURR-Mbps",
    id: "CURR-Mbps",
    title: "Current speed in Mbps",
    numeric: true,
    renderCell: (value) => <NumberCell value={value} hasNA={true} />,
  },
  {
    label: "MEAN-Mbps",
    id: "MEAN-Mbps",
    title:
      "Moving average of the mean speed (calculated every 10 minutes) in Mbps.",
    numeric: true,
    cellClassName: quartileColorCellClassName,
    quantiles: metricsQuantileData["MEAN-Mbps"],
    inverted: true,
    threshold: thresholds["MEAN-Mbps"],
    renderCell: (value) => (
      <NumberCell
        precision={RATE_PRECISIONS}
        value={value}
        hasNA={true}
      />
    ),
  },
  {
    id: "RTT-80%-ms",
    label: "RTT-P80-ms",
    title:
      "The 80-th percentile of all RTT latency, measured from BQN to end user, in 5 minutes.",
    renderCell: (value) => (
      <NumberCell value={value} hasNA={true} precision={1} />
    ),
    numeric: true,
    quantiles: metricsQuantileData["RTT-80%-ms"],
    threshold: thresholds["RTT-80%-ms"],
    inverted: false,
    cellClassName: quartileColorCellClassName,
    mean: thresholds["MEAN-Mbps"],
    percent: thresholds["PERCENT-WARNING"],
  },
  {
    id: "RTT-MED-ms",
    label: "RTT-MED-ms",
    title:
      "The 50-th percentile (median) of all RTT latency samples, measured from BQN to end user, in 5 minutes.",
    renderCell: (value) => (
      <NumberCell value={value} hasNA={true} precision={1} />
    ),
    numeric: true,
    quantiles: metricsQuantileData["RTT-MED-ms"],
    threshold: thresholds["RTT-MED-ms"],
    inverted: false,
    cellClassName: quartileColorCellClassName,
    mean: thresholds["MEAN-Mbps"],
    percent: thresholds["PERCENT-WARNING"],
  },
  {
    id: "RTT-MIN-ms",
    label: "RTT-MIN-ms",
    title:
      "The mean of all RTT minimum latency samples, measured in 1-second intervals, during active downloads, in 5 minutes.",
    renderCell: (value) => (
      <NumberCell value={value} hasNA={true} precision={1} />
    ),
    numeric: true,
    quantiles: metricsQuantileData["RTT-MIN-ms"],
    threshold: thresholds["RTT-MIN-ms"],
    inverted: false,
    cellClassName: quartileColorCellClassName,
    mean: thresholds["MEAN-Mbps"],
    percent: thresholds["PERCENT-WARNING"],
  },
  {
    label: "RTX",
    id: "RTX",
    title:
      "Moving average of the percentage of TCP retransmissions (which reflect packet losses in this direction)",
    numeric: true,
    cellClassName: quartileColorCellClassName,
    quantiles: metricsQuantileData["RTX"],
    threshold: thresholds["RTX"],
    mean: thresholds["MEAN-Mbps"],
    inverted: false,
    renderCell: (value) => (
      <NumberCell precision={1} value={value} hasNA={true} percent={true} />
    ),
  },
  {
    label: "CONGESTION",
    id: "CONGEST",
    title: "Moving average of percentage of traffic suffering congestion",
    numeric: true,
    quantiles: metricsQuantileData["CONGEST"],
    threshold: thresholds["CONGEST"],
    mean: thresholds["MEAN-Mbps"],
    inverted: false,
    renderCell: (value) => (
      <NumberCell value={value} hasNA={true} percent={true} precision={1} />
    ),
    cellClassName: quartileColorCellClassName,
  },
  {
    label: "WARN",
    title: "Number of warnings",
    id: "WARN",
    numeric: true,
    renderCell: (value) => (
      <NumberCell
        value={value}
        hasNA={true}
        precision={0}
      />
    ),
  },
  {
    label: "LIFETIME",
    id: "LIFETIME",
    title: "Duration so far of this subscriber session",
    numeric: true,
    parse: (data) => (data === "n/a" || !data ? 0 : durationToInt(data)),
  },
  {
    label: "GROUP-TYPE",
    title: "Subscriber group type",
    id: "GROUP-TYPE",
    numeric: false,
  },
  ...(meanSorting !== "default"
    ? [
        {
          label: "MEAN SPEC",
          id: "MEAN-SPEC",
          title: "Mean Value",
          numeric: true,
          hidden: true,
          parse: (value) => parseFloat(value),
        },
      ]
    : []),
  {
    label: "ACTIONS",
    id: "ACTIONS",
    actions: [
      {
        label: "Edit",
        icon: "edit",
        onClick: (row) => {
          actions.send("subscriber-group-edit-clicked", {
            subscriberGroup: row["SUBSCRIBER-GROUP"],
          });
        },
      },
      {
        label: "Delete",
        icon: "delete",
        onClick: (row) => {
          setDeleteSelected([row["SUBSCRIBER-GROUP"]]);
          setShowDeleteModal(true);
        },
      },
      {
        label: "Thoughput",
        icon: "search",
        onClick: (row) => {
          globalNavigate("viewStatusSubscriberGroups", {
            subscriberGroup: row["SUBSCRIBER-GROUP"],
            view: "throughput",
            timePeriod,
            metric,
            direction,
            type: row["GROUP-TYPE"],
          });
        },
      },
    ],
  },
];

const doesNothing = () => {};

function ListSubscriberGroups({
  tableData,
  colorize,
  timePeriod,
  metric,
  direction,
  thresholds,
  onRemoved = doesNothing,
  selected,
  setSelected,
  meanSorting,
  fieldsMeanMap,
  metricsQuantileData,
}) {
  const actions = useContext(ActionsContext);
  const profile = useProfile();
  const { hideRates = false } = profile || {};
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);

  const tableClassName = colorize ? "colored" : "notColored";
  const columns = useMemo(() => {
    return getColumns({
      timePeriod,
      metric,
      direction,
      metricsQuantileData,
      thresholds,
      actions,
      onRemoved,
      hideRates,
      meanSorting,
        setDeleteSelected,
        setShowDeleteModal,
    });
  }, [
    timePeriod,
    metric,
    direction,
    metricsQuantileData,
    thresholds,
    actions,
    onRemoved,
    hideRates,
    meanSorting,
      setDeleteSelected,
      setShowDeleteModal,
  ]);

  const tableDataSpec = useMemo(() => {
    return tableData.map((row) => {
      const meanValue = fieldsMeanMap[row["SUBSCRIBER-GROUP"]];
      const calculatedRow = {
        ...row,
        "WARN": getWarningCount(thresholds, row, metricsQuantileData)
      };

      if (meanSorting !== "default") {
        calculatedRow["MEAN-SPEC"] = meanValue !== undefined ? meanValue : "n/a";
      }

      return calculatedRow;
    });
  }, [tableData, fieldsMeanMap, meanSorting, thresholds, metricsQuantileData]);

  const orderByDefault = meanSorting !== "default" ? "MEAN-SPEC" : "";
  const orderDefaultType = meanSorting !== "default" ? "numeric" : "";

  if (profile === null) {
    return null;
  }

  return (
    <div>
      <TableContainer className={tableClassName}>
        <MuiTable
          isSelectable={true}
          isRowSelectable={false}
          data={tableDataSpec}
          sort={true}
          columns={columns}
          search={true}
          selected={selected}
          setSelected={setSelected}
          csvPrefix="subs-groups"
          rowsPerPageNr={25}
          orderDefault={meanSorting !== "default" ? meanSorting : ""}
          orderByDefault={orderByDefault}
          orderDefaultType={orderDefaultType}
          deleteSelected={(values) => {
            const selectedItems = tableDataSpec
              .filter((row) => values.includes(row.id))
              .map((row) => row["SUBSCRIBER-GROUP"]);
            setDeleteSelected(selectedItems);
            setShowDeleteModal(true);
          }}
        />
      </TableContainer>
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        whenDone={() => {
          setSelected([]);
          actions.send("do-load");
        }}
      />
    </div>
  );
}

export default ListSubscriberGroups;
